import { Elements, PaymentElement, useElements, useStripe as useStripeAPI } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useStripe } from 'contexts/stripe';
import { useViewport } from 'contexts/viewport';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import stripeService from 'services/stripe';
import userService from 'services/user';
import {
    Box,
    Button,
    Divider,
    Form,
    Icon,
    IconButton,
    Link,
    Loading,
    Paper,
    ResponsiveDialog,
    Stack,
    styled,
    Switch,
    Text,
} from 'shell/components';
import theme from 'shell/themes';
import { getCardSvg } from 'utils/helper';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const stripeOptions = {
    appearance: {
        variables: {
            fontSizeBase: `${theme.typography.p2.fontSize.large}rem`,
        },
    },
    currency: 'usd',
    mode: 'setup',
};

const PaymentForm = ({ paymentMethod, setPaymentMethod }) => {
    const { customer, setEntitlements } = useApp();
    const { currentUser } = useAuth();
    const elements = useElements();
    const { paymentDialog, setPaymentDialog, goToPrevStep, upgradeSubscription } = useStripe();
    const stripeAPI = useStripeAPI();

    const handleSubmit = async () => {
        if (!stripeAPI || !elements) return;
        setPaymentDialog((current) => ({ ...current, isLoading: true }));
        try {
            await elements.submit();
            const { client_secret: clientSecret } = await stripeService.createSetupIntent(customer.id, {
                action: 'update_subscription',
                is_annual: paymentDialog.isAnnual,
            });
            const { error } = await stripeAPI.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: window.location.href,
                },
            });
            if (error) throw error;
        } catch (e) {
            console.error(e);
            setPaymentDialog((current) => ({ ...current, isLoading: false }));
        }
    };

    const onClickConfirm = async () => {
        setPaymentDialog((current) => ({
            ...current,
            isLoading: true,
        }));
        await upgradeSubscription(paymentDialog.isAnnual);
        setTimeout(async () => {
            const entitlements = await userService.getUserEntitlements(currentUser.id, customer.id);
            setEntitlements(entitlements);
            setPaymentDialog((current) => ({
                ...current,
                isLoading: false,
                stepIndex: 2,
            }));
        }, 1000);
    };

    if (typeof paymentMethod === 'undefined') return <Loading sx={{ my: '20vh' }} />;

    if (paymentMethod) {
        const cardSvg = getCardSvg(paymentMethod.card.brand);
        const today = moment(new Date().getTime());
        const expiryDate = moment(
            new Date(`${paymentMethod.card.exp_month}/1/${paymentMethod.card.exp_year}`).getTime()
        ).endOf('month');
        const isExpired = today.isAfter(expiryDate);

        return (
            <Stack spacing={4}>
                <Paper variant={'outlined'}>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            {cardSvg && <Box sx={{ mx: -8, my: -2, scale: '0.5' }}>{cardSvg}</Box>}
                            <Stack spacing={1}>
                                <Text sx={{ textTransform: 'capitalize' }} weight={'semibold'}>
                                    {paymentMethod.card.brand} •••• {paymentMethod.card.last4}
                                </Text>
                                {isExpired ? (
                                    <Text
                                        sx={(theme) => ({
                                            borderRadius: '4px',
                                            width: 'fit-content',
                                            px: 2,
                                            py: 1,
                                            background: theme.palette.ensightful.error[50],
                                        })}
                                        color={'error'}
                                    >
                                        Expired {paymentMethod.card.exp_month}/
                                        {paymentMethod.card.exp_year.toString().substring(2, 4)}
                                    </Text>
                                ) : (
                                    <Text color={'light'}>
                                        Expires {paymentMethod.card.exp_month}/
                                        {paymentMethod.card.exp_year.toString().substring(2, 4)}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                        <Button variant={'borderless'} onClick={() => setPaymentMethod(null)}>
                            Update
                        </Button>
                    </Stack>
                </Paper>
                <Stack direction={'row-reverse'} spacing={4}>
                    <Button loading={paymentDialog.isLoading} onClick={onClickConfirm}>
                        Confirm & Pay
                    </Button>
                    <Button color={'neutral'} variant={'tertiary'} onClick={goToPrevStep}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        );
    }
    return (
        <Form onSubmit={handleSubmit}>
            <Stack spacing={4}>
                <PaymentElement />
                <Stack direction={'row-reverse'} spacing={4}>
                    <Button loading={paymentDialog.isLoading} type={'submit'}>
                        Confirm & Pay
                    </Button>
                    <Button color={'neutral'} variant={'tertiary'} onClick={goToPrevStep}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </Form>
    );
};

const PaymentDialog = () => {
    const { currentUser } = useAuth();
    const { customer, subscription, entitlements } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const {
        paymentDialog,
        setPaymentDialog,
        goToNextStep,
        goToPrevStep,
        cancelAtPeriodEnd,
        scheduleSubscriptionBillingPeriodChange,
    } = useStripe();
    const { device } = useViewport();

    const { open, stepIndex } = paymentDialog;

    const [paymentMethod, setPaymentMethod] = useState();

    let currentPlan; // 'free', 'monthly', or 'annual'
    if (subscription?.status === 'trialing' || !entitlements?.length) {
        currentPlan = 'free';
    }
    if (
        ['active', 'past_due'].includes(subscription?.status) &&
        entitlements?.length > 0 &&
        subscription?.plan?.interval === 'month'
    ) {
        currentPlan = 'monthly';
    }
    if (
        ['active', 'past_due'].includes(subscription?.status) &&
        entitlements?.length > 0 &&
        subscription?.plan?.interval === 'year'
    ) {
        currentPlan = 'annual';
    }

    const onClose = () => {
        setPaymentDialog((current) => ({ ...current, open: false }));
        history.replace({ search: '', state: undefined });
    };

    const onClickSwitchToFree = () => {
        setDialog({
            open: true,
            message: 'Switch to Free Plan?',
            description:
                'Your Starter Plan subscription will be canceled, but you’ll keep access until the end of your billing period.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Switch',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                cancelAtPeriodEnd();
                closeDialog();
                setPaymentDialog((current) => ({ ...current, open: false }));
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickSwitchToStarter = () => {
        if (currentPlan === 'free') {
            goToNextStep();
        } else {
            setDialog({
                open: true,
                message: `Switch to ${paymentDialog.isAnnual ? 'Annual' : 'Monthly'} Billing?`,
                description: (
                    <Stack spacing={3}>
                        <Text>
                            Your plan will switch to {paymentDialog.isAnnual ? 'annual' : 'monthly'} billing at the end
                            of your current billing period on{' '}
                            {moment(new Date(subscription.current_period_end * 1000).getTime()).format('MMM D, YYYY')}.
                        </Text>
                        <Text>
                            Your first {paymentDialog.isAnnual ? 'annual' : 'monthly'} payment of $
                            {paymentDialog.isAnnual ? '120' : '12'} will be charged on this date.
                        </Text>
                    </Stack>
                ),
                note: '',
                variant: 'primary',
                actionButtonText: 'Switch',
                cancelButtonText: 'Cancel',
                onAction: async () => {
                    closeDialog();
                    setPaymentDialog((current) => ({ ...current, open: false, stepIndex: 0 }));
                    await scheduleSubscriptionBillingPeriodChange();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    useEffect(() => {
        if (paymentDialog.open) {
            (async () => {
                const paymentMethod = await userService.getUserPaymentMethod(currentUser.id, customer.id);
                setPaymentMethod(paymentMethod);
            })();
        }
    }, [currentUser?.id, customer?.id, paymentDialog.open]);

    return (
        <CustomResponsiveDialog device={device} open={open} onClose={onClose}>
            {stepIndex === 0 && (
                <Stack alignItems={'center'} spacing={6}>
                    <Stack sx={{ position: 'relative', width: '100%' }} direction={'row'} justifyContent={'center'}>
                        <Text component={'h1'} variant={'h5'} weight={'semibold'}>
                            {device === 'mobile' ? 'Choose a plan' : 'Choose the plan that’s right for you'}
                        </Text>
                        <IconButton
                            sx={{ position: 'absolute', top: 0, right: 0 }}
                            aria-label={'close'}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={onClose}
                        />
                    </Stack>
                    <Grid>
                        <Text sx={{ justifySelf: 'end' }} color={'light'} weight={'semibold'}>
                            Monthly
                        </Text>
                        <Switch
                            checked={paymentDialog.isAnnual}
                            onChange={() => {
                                setPaymentDialog((current) => ({ ...current, isAnnual: !current.isAnnual }));
                            }}
                        />
                        <Text sx={{ justifySelf: 'start' }} weight={'semibold'}>
                            Annual{' '}
                            <Text color={'success'} component={'span'}>
                                SAVE 17%!
                            </Text>
                        </Text>
                    </Grid>
                    <Stack sx={{ width: '100%' }} direction={device === 'desktop' ? 'row' : 'column'} spacing={5}>
                        <CustomPaper variant={'outlined'}>
                            <Stack divider={<Divider />} spacing={5}>
                                <Stack spacing={5}>
                                    <Stack spacing={2}>
                                        <Text variant={'p1'} weight={'semibold'}>
                                            Free Plan
                                        </Text>
                                        <Text color={'light'}>
                                            Explore AI integration to simplify your assignments.{' '}
                                            <Link
                                                external
                                                href={process.env.REACT_APP_TOS}
                                                rel={'noopener noreferrer'}
                                                target={'_blank'}
                                                variant={'neutral'}
                                            >
                                                Terms apply.
                                            </Link>
                                        </Text>
                                    </Stack>
                                    <Stack spacing={4}>
                                        <Text component={'p'} variant={'h4'} weight={'semibold'}>
                                            Free
                                        </Text>
                                        <Button disabled={currentPlan === 'free'} onClick={onClickSwitchToFree}>
                                            {currentPlan === 'free' ? 'Your Current Plan' : 'Switch to Free'}
                                        </Button>
                                    </Stack>
                                </Stack>
                                <Stack spacing={5}>
                                    <Text color={'primary'}>Included in the Free Plan:</Text>
                                    <Stack spacing={3}>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Course & assignment setup</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>AI grade fine-tuning</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Preview AI grading & feedback</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Fully customizable rubrics</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>AI rubric generator</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>WCAG AA & FERPA compliant</Text>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </CustomPaper>
                        <CustomPaper variant={'outlined'}>
                            <Stack divider={<Divider />} spacing={5}>
                                <Stack spacing={5}>
                                    <Stack spacing={2}>
                                        <Text variant={'p1'} weight={'semibold'}>
                                            Starter Plan
                                        </Text>
                                        <Text color={'light'}>
                                            Unlimited access to all grading and feedback tools.{' '}
                                            <Link
                                                external
                                                href={process.env.REACT_APP_TOS}
                                                rel={'noopener noreferrer'}
                                                target={'_blank'}
                                                variant={'neutral'}
                                            >
                                                Terms apply.
                                            </Link>
                                        </Text>
                                    </Stack>
                                    <Stack spacing={4}>
                                        {paymentDialog.isAnnual ? (
                                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                                <Text component={'p'} variant={'h4'} weight={'semibold'}>
                                                    <Text
                                                        sx={{ textDecoration: 'line-through' }}
                                                        color={'disabled'}
                                                        component={'span'}
                                                        variant={'h4'}
                                                    >
                                                        $12
                                                    </Text>{' '}
                                                    $10
                                                </Text>
                                                <Stack>
                                                    <Text component={'span'}>/month</Text>
                                                    <Text component={'span'}>billed annually</Text>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <Text component={'p'} variant={'h4'} weight={'semibold'}>
                                                $12
                                                <Text sx={{ verticalAlign: 'text-top' }} component={'span'}>
                                                    {' '}
                                                    /month
                                                </Text>
                                            </Text>
                                        )}
                                        <Button
                                            disabled={
                                                paymentDialog.isAnnual
                                                    ? currentPlan === 'annual'
                                                    : currentPlan === 'monthly'
                                            }
                                            onClick={onClickSwitchToStarter}
                                        >
                                            {currentPlan === 'free'
                                                ? 'Upgrade to Starter'
                                                : paymentDialog.isAnnual
                                                  ? currentPlan === 'monthly'
                                                      ? 'Switch to Annual Billing'
                                                      : 'Your Current Plan'
                                                  : currentPlan === 'annual'
                                                    ? 'Switch to Monthly Billing'
                                                    : 'Your Current Plan'}
                                            {}
                                        </Button>
                                    </Stack>
                                </Stack>
                                <Stack spacing={5}>
                                    <Text color={'primary'}>Everything in Free, plus:</Text>
                                    <Stack spacing={3}>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Unlimited courses</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Unlimited bulk uploads</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text component={'i'}>Grade beyond just essays</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Content bank</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Multiple graders</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Personalized support</Text>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </CustomPaper>
                        <CustomPaper variant={'outlined'}>
                            <Stack divider={<Divider />} spacing={5}>
                                <Stack spacing={5}>
                                    <Stack spacing={2}>
                                        <Text variant={'p1'} weight={'semibold'}>
                                            Institution Plan
                                        </Text>
                                        <Text color={'light'}>
                                            All tools, admin controls, and LMS integration.{' '}
                                            <Link
                                                external
                                                href={process.env.REACT_APP_TOS}
                                                rel={'noopener noreferrer'}
                                                target={'_blank'}
                                                variant={'neutral'}
                                            >
                                                Terms apply.
                                            </Link>
                                        </Text>
                                    </Stack>
                                    <Stack spacing={4}>
                                        <Text component={'p'} variant={'h4'} weight={'semibold'}>
                                            Let’s talk
                                        </Text>
                                        <Link
                                            external
                                            href={'https://calendly.com/chrisdu/30min'}
                                            rel={'noopener noreferrer'}
                                            target={'_blank'}
                                        >
                                            <Button sx={{ width: '100%' }}>Contact Sales</Button>
                                        </Link>
                                    </Stack>
                                </Stack>
                                <Stack spacing={5}>
                                    <Text color={'primary'}>Everything in Starter, plus:</Text>
                                    <Stack spacing={3}>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Administrator access</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Accreditation data export</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Instant student feedback</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>LMS API integration</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>Grade quizzes and exams</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Icon kind={'checkmark'} />
                                            <Text>24/7 dedicated support</Text>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </CustomPaper>
                    </Stack>
                </Stack>
            )}
            {stepIndex === 1 && (
                <GridBox device={device}>
                    <LeftStack device={device}>
                        <Button
                            sx={{ alignSelf: 'flex-start' }}
                            startIcon={<Icon kind={'arrowLeft'} />}
                            variant={'borderless'}
                            onClick={goToPrevStep}
                        >
                            Back
                        </Button>
                        <InputStack device={device} spacing={5}>
                            <Stack spacing={1}>
                                <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                                    {paymentMethod ? 'Finalize' : 'Add'} Payment Details
                                </Text>
                                <Text>
                                    {paymentMethod
                                        ? 'Your saved payment method will be used. If you’d like to use a change it, you can update it now.'
                                        : 'Payment details will be saved to your account.'}
                                </Text>
                            </Stack>
                            {device === 'mobile' && (
                                <Stack
                                    sx={(theme) => ({
                                        borderRadius: '12px',
                                        boxShadow: theme.palette.ensightful.shadows[1],
                                    })}
                                >
                                    <Stack
                                        sx={(theme) => ({
                                            borderRadius: '12px 12px 0 0',
                                            padding: theme.spacing(8, 7),
                                            background: theme.palette.ensightful.primary[500],
                                            color: theme.palette.ensightful.shades[0],
                                        })}
                                    >
                                        <Text color={'inherit'} variant={'p1'} weight={'semibold'}>
                                            Starter Plan
                                        </Text>
                                        <Text color={'inherit'}>
                                            Unlimited access to all grading and feedback tools.{' '}
                                            <Link
                                                sx={{ color: 'inherit' }}
                                                external
                                                href={process.env.REACT_APP_TOS}
                                                rel={'noopener noreferrer'}
                                                target={'_blank'}
                                                variant={'neutral'}
                                            >
                                                Terms apply.
                                            </Link>
                                        </Text>
                                    </Stack>
                                    <Stack
                                        sx={(theme) => ({
                                            borderRadius: '0 0 12px 12px',
                                            padding: theme.spacing(8, 7),
                                            background: theme.palette.ensightful.shades[0],
                                        })}
                                        divider={<Divider />}
                                        spacing={4}
                                    >
                                        <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                            <Switch
                                                checked={paymentDialog.isAnnual}
                                                onChange={(e) => {
                                                    setPaymentDialog((current) => ({
                                                        ...current,
                                                        isAnnual: e.target.checked,
                                                    }));
                                                }}
                                            />
                                            <Text weight={'semibold'}>Annual</Text>
                                            <Text color={'success'}>SAVE 17%!</Text>
                                        </Stack>
                                        <Stack spacing={2}>
                                            <Stack direction={'row'} justifyContent={'space-between'}>
                                                <Text>${paymentDialog.isAnnual ? 10 : 12}/month</Text>
                                                <Text>${paymentDialog.isAnnual ? 10 : 12}.00 USD</Text>
                                            </Stack>
                                            <Text component={'ul'}>
                                                <Text color={'light'} component={'li'}>
                                                    Recurring monthly billing starting today
                                                </Text>
                                                <Text color={'light'} component={'li'}>
                                                    Cancel anytime.{' '}
                                                    <Link
                                                        sx={{ color: 'inherit' }}
                                                        external
                                                        href={process.env.REACT_APP_TOS}
                                                        rel={'noopener noreferrer'}
                                                        target={'_blank'}
                                                        variant={'neutral'}
                                                    >
                                                        Terms apply
                                                    </Link>
                                                </Text>
                                            </Text>
                                        </Stack>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Text>Tax</Text>
                                            <Text>$0 USD</Text>
                                        </Stack>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Text variant={'p1'} weight={'semibold'}>
                                                Due today
                                            </Text>
                                            <Text variant={'p1'} weight={'semibold'}>
                                                ${paymentDialog.isAnnual ? 120 : 12}.00 USD
                                            </Text>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            )}
                            <Elements stripe={stripePromise} options={stripeOptions}>
                                <PaymentForm paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} />
                            </Elements>
                            <Text color={'light'} variant={'p3'}>
                                By continuing, you agree to our{' '}
                                <Link
                                    external
                                    href={process.env.REACT_APP_TOS}
                                    rel={'noopener noreferrer'}
                                    target={'_blank'}
                                    variant={'neutral'}
                                >
                                    Terms of Service
                                </Link>{' '}
                                and acknowledge that you have read and understood our{' '}
                                <Link
                                    external
                                    href={process.env.REACT_APP_PRIVACY_POLICY}
                                    rel={'noopener noreferrer'}
                                    target={'_blank'}
                                    variant={'neutral'}
                                >
                                    Privacy Policy
                                </Link>
                                .
                            </Text>
                        </InputStack>
                    </LeftStack>
                    <RightStack device={device}>
                        <IconButton
                            sx={{ alignSelf: 'flex-end' }}
                            aria-label={'close'}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={onClose}
                        />
                        <Stack sx={{ mt: 16, mx: 8, mb: 12 }}>
                            <Stack
                                sx={(theme) => ({
                                    borderRadius: '12px 12px 0 0',
                                    padding: theme.spacing(8, 7),
                                    background: theme.palette.ensightful.primary[500],
                                    color: theme.palette.ensightful.shades[0],
                                })}
                            >
                                <Text color={'inherit'} variant={'p1'} weight={'semibold'}>
                                    Starter Plan
                                </Text>
                                <Text color={'inherit'}>
                                    Unlimited access to all grading and feedback tools.{' '}
                                    <Link sx={{ color: 'inherit' }} to={process.env.REACT_APP_TOS} variant={'neutral'}>
                                        Terms apply.
                                    </Link>
                                </Text>
                            </Stack>
                            <Stack
                                sx={(theme) => ({
                                    borderRadius: '0 0 12px 12px',
                                    padding: theme.spacing(8, 7),
                                    background: theme.palette.ensightful.shades[0],
                                })}
                                divider={<Divider />}
                                spacing={4}
                            >
                                <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                    <Switch
                                        checked={paymentDialog.isAnnual}
                                        onChange={(e) => {
                                            setPaymentDialog((current) => ({
                                                ...current,
                                                isAnnual: e.target.checked,
                                            }));
                                        }}
                                    />
                                    <Text weight={'semibold'}>Annual</Text>
                                    <Text color={'success'}>SAVE 17%!</Text>
                                </Stack>
                                <Stack spacing={2}>
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Text>${paymentDialog.isAnnual ? 10 : 12}/month</Text>
                                        <Text>${paymentDialog.isAnnual ? 10 : 12}.00 USD</Text>
                                    </Stack>
                                    <Text component={'ul'}>
                                        <Text color={'light'} component={'li'}>
                                            Recurring monthly billing starting today
                                        </Text>
                                        <Text color={'light'} component={'li'}>
                                            Cancel anytime.{' '}
                                            <Link
                                                sx={{ color: 'inherit' }}
                                                to={process.env.REACT_APP_TOS}
                                                variant={'neutral'}
                                            >
                                                Terms apply
                                            </Link>
                                        </Text>
                                    </Text>
                                </Stack>
                                <Stack direction={'row'} justifyContent={'space-between'}>
                                    <Text>Tax</Text>
                                    <Text>$0 USD</Text>
                                </Stack>
                                <Stack direction={'row'} justifyContent={'space-between'}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        Due today
                                    </Text>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        ${paymentDialog.isAnnual ? 100 : 12}.00 USD
                                    </Text>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack sx={{ mb: 4 }} alignItems={'center'}>
                            {StripeSVG}
                        </Stack>
                    </RightStack>
                </GridBox>
            )}
            {stepIndex === 2 && (
                <Stack>
                    <IconButton
                        sx={{ alignSelf: 'flex-end' }}
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                    <Stack sx={{ mt: 8, mb: 24 }} alignItems={'center'} spacing={5}>
                        {CompletedSVG}
                        <Stack spacing={7}>
                            <Stack sx={{ maxWidth: '400px' }} spacing={3}>
                                <Text align={'center'} component={'h1'} variant={'h6'} weight={'semibold'}>
                                    You’re All Set!
                                </Text>
                                <Text align={'center'}>
                                    Your{' '}
                                    <Text component={'span'} weight={'semibold'}>
                                        Starter Plan
                                    </Text>{' '}
                                    subscription is now active, and you have full access to all Starter Plan features.
                                    You can manage your subscription, update payment details, or make changes to your
                                    plan anytime in{' '}
                                    <Text component={'span'} weight={'semibold'}>
                                        Account Settings
                                    </Text>
                                    .
                                </Text>
                            </Stack>
                            <Button sx={{ alignSelf: 'center', width: 'fit-content' }} onClick={onClose}>
                                Get Started
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </CustomResponsiveDialog>
    );
};

const StripeSVG = (
    <svg width='151' height='34' viewBox='0 0 151 34' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g clipPath='url(#clip0_8958_58544)'>
            <path
                d='M146.5 0H4.23C3.24074 0 2.292 0.392981 1.59249 1.09249C0.892981 1.792 0.5 2.74074 0.5 3.73L0.5 30.27C0.5 31.2593 0.892981 32.208 1.59249 32.9075C2.292 33.607 3.24074 34 4.23 34H146.5C147.561 34 148.578 33.5786 149.328 32.8284C150.079 32.0783 150.5 31.0609 150.5 30V4C150.5 2.93913 150.079 1.92172 149.328 1.17157C148.578 0.421427 147.561 0 146.5 0ZM149.5 30C149.5 30.7956 149.184 31.5587 148.621 32.1213C148.059 32.6839 147.296 33 146.5 33H4.23C3.50677 32.9974 2.81391 32.7089 2.30251 32.1975C1.7911 31.6861 1.50263 30.9932 1.5 30.27V3.73C1.50263 3.00677 1.7911 2.31391 2.30251 1.80251C2.81391 1.2911 3.50677 1.00263 4.23 1H146.5C147.296 1 148.059 1.31607 148.621 1.87868C149.184 2.44129 149.5 3.20435 149.5 4V30Z'
                fill='white'
            />
            <path
                d='M17.5695 11.2402H13.2695V22.0002H15.1895V17.8402H17.5695C19.9695 17.8402 21.4695 16.6802 21.4695 14.5402C21.4695 12.4002 19.9695 11.2402 17.5695 11.2402ZM17.4695 16.2402H15.1895V12.9402H17.4995C18.8795 12.9402 19.6095 13.5302 19.6095 14.5902C19.6095 15.6502 18.8495 16.1902 17.4995 16.1902L17.4695 16.2402Z'
                fill='white'
            />
            <path
                d='M25.6002 14.0003C25.0744 13.9942 24.5533 14.0981 24.0701 14.3054C23.5869 14.5127 23.1525 14.8188 22.7946 15.2039C22.4368 15.5891 22.1634 16.0449 21.9922 16.5419C21.8209 17.039 21.7555 17.5664 21.8002 18.0903C21.7533 18.617 21.8166 19.1477 21.986 19.6486C22.1554 20.1496 22.4272 20.6098 22.7842 20.9999C23.1412 21.3901 23.5754 21.7016 24.0594 21.9148C24.5433 22.1279 25.0663 22.238 25.5952 22.238C26.124 22.238 26.647 22.1279 27.1309 21.9148C27.6149 21.7016 28.0491 21.3901 28.4061 20.9999C28.7631 20.6098 29.0349 20.1496 29.2043 19.6486C29.3738 19.1477 29.437 18.617 29.3902 18.0903C29.4363 17.5669 29.3723 17.0397 29.2022 16.5427C29.0321 16.0456 28.7597 15.5897 28.4026 15.2044C28.0455 14.819 27.6117 14.5128 27.129 14.3054C26.6463 14.098 26.1255 13.994 25.6002 14.0003ZM25.6002 20.6703C24.3802 20.6703 23.6002 19.6703 23.6002 18.0903C23.6002 16.5103 24.3602 15.5103 25.6002 15.5103C26.8402 15.5103 27.6002 16.5103 27.6002 18.0903C27.6002 19.6703 26.8102 20.6603 25.6002 20.6603V20.6703Z'
                fill='white'
            />
            <path
                d='M37.2797 19.3499L35.8697 14.1299H34.3897L32.9897 19.3499L31.5697 14.1299H29.7197L32.0897 22.0099H33.6497L35.0897 16.8499L36.5297 22.0099H38.0897L40.4597 14.1299H38.6797L37.2797 19.3499Z'
                fill='white'
            />
            <path
                d='M44.5002 14C43.9814 14.0096 43.4699 14.1246 42.9968 14.3379C42.5238 14.5513 42.099 14.8585 41.7483 15.241C41.3976 15.6235 41.1283 16.0733 40.9567 16.563C40.7851 17.0528 40.7149 17.5723 40.7502 18.09C40.7084 18.6157 40.7768 19.1444 40.951 19.6422C41.1252 20.14 41.4014 20.596 41.7619 20.9809C42.1223 21.3659 42.5592 21.6714 43.0445 21.8778C43.5298 22.0843 44.0529 22.1872 44.5802 22.18C45.3052 22.2191 46.0242 22.0297 46.6359 21.6386C47.2477 21.2476 47.7213 20.6744 47.9902 20L46.5002 19.38C46.3833 19.7664 46.1385 20.1016 45.8059 20.3306C45.4734 20.5595 45.0729 20.6686 44.6702 20.64C44.3834 20.6468 44.0981 20.5954 43.8317 20.4888C43.5654 20.3821 43.3234 20.2226 43.1205 20.0197C42.9176 19.8168 42.7581 19.5748 42.6514 19.3085C42.5448 19.0421 42.4934 18.7568 42.5002 18.47H48.0202V17.87C48.0402 15.71 46.8202 14 44.5002 14ZM42.5702 17.13C42.6403 16.6708 42.8745 16.2526 43.2294 15.9529C43.5843 15.6532 44.0358 15.4923 44.5002 15.5C44.7209 15.4813 44.943 15.5099 45.1518 15.5837C45.3606 15.6576 45.5512 15.775 45.7111 15.9283C45.871 16.0815 45.9964 16.2671 46.079 16.4726C46.1616 16.678 46.1995 16.8987 46.1902 17.12L42.5702 17.13Z'
                fill='white'
            />
            <path
                d='M51.1906 15.3005V14.1305H49.3906V22.0005H51.1906V17.8705C51.1746 17.6036 51.2153 17.3364 51.3102 17.0865C51.405 16.8366 51.5518 16.6096 51.7408 16.4206C51.9298 16.2316 52.1568 16.0848 52.4067 15.99C52.6566 15.8952 52.9238 15.8544 53.1906 15.8705C53.4104 15.8549 53.6309 15.8549 53.8506 15.8705V14.0705C53.7106 14.0705 53.5506 14.0705 53.3406 14.0705C52.902 14.0477 52.4662 14.1516 52.085 14.3697C51.7038 14.5878 51.3933 14.9109 51.1906 15.3005Z'
                fill='white'
            />
            <path
                d='M57.9797 14C57.4609 14.0096 56.9494 14.1246 56.4763 14.3379C56.0033 14.5513 55.5785 14.8585 55.2278 15.241C54.8771 15.6235 54.6078 16.0733 54.4362 16.563C54.2646 17.0528 54.1944 17.5723 54.2297 18.09C54.1879 18.6157 54.2563 19.1444 54.4305 19.6422C54.6047 20.14 54.8809 20.596 55.2414 20.9809C55.6018 21.3659 56.0387 21.6714 56.524 21.8778C57.0093 22.0843 57.5324 22.1872 58.0597 22.18C58.7781 22.2106 59.4882 22.0172 60.0919 21.6267C60.6956 21.2361 61.163 20.6677 61.4297 20L59.8897 19.41C59.7728 19.7964 59.5279 20.1316 59.1954 20.3606C58.8629 20.5895 58.4624 20.6986 58.0597 20.67C57.7787 20.6674 57.5011 20.609 57.2429 20.4982C56.9847 20.3874 56.7511 20.2263 56.5557 20.0244C56.3603 19.8225 56.2069 19.5837 56.1046 19.322C56.0023 19.0603 55.9531 18.7809 55.9597 18.5H61.4997V17.9C61.4997 15.71 60.2597 14 57.9797 14ZM56.0497 17.13C56.1217 16.6744 56.3554 16.2599 56.708 15.9624C57.0606 15.6649 57.5084 15.5043 57.9697 15.51C58.1904 15.4913 58.4125 15.5199 58.6213 15.5937C58.83 15.6676 59.0207 15.785 59.1806 15.9383C59.3405 16.0915 59.4659 16.2771 59.5485 16.4826C59.6311 16.688 59.669 16.9087 59.6597 17.13H56.0497Z'
                fill='white'
            />
            <path
                d='M68.0601 15.0002C67.7826 14.6746 67.4353 14.4156 67.0441 14.2425C66.6529 14.0694 66.2277 13.9866 65.8001 14.0002C63.5901 14.0002 62.3301 15.8502 62.3301 18.0902C62.3301 20.3302 63.5901 22.1802 65.8001 22.1802C66.228 22.1961 66.6539 22.1143 67.0455 21.941C67.437 21.7677 67.784 21.5076 68.0601 21.1802V22.0002H69.8601V11.2402H68.0601V15.0002ZM68.0601 18.3502C68.1004 18.6354 68.0787 18.9258 67.9964 19.2018C67.9141 19.4778 67.7732 19.7327 67.5833 19.9492C67.3934 20.1657 67.159 20.3386 66.8961 20.4562C66.6332 20.5737 66.348 20.6331 66.0601 20.6302C64.7501 20.6302 64.0601 19.6302 64.0601 18.1102C64.0601 16.5902 64.7601 15.5902 66.0601 15.5902C67.1701 15.5902 68.0601 16.4002 68.0601 17.8802V18.3502Z'
                fill='white'
            />
            <path
                d='M79.8102 14.0002C79.3746 13.9812 78.9403 14.0613 78.5402 14.2345C78.14 14.4077 77.7844 14.6696 77.5002 15.0002V11.2402H75.7002V22.0002H77.5002V21.1702C77.7792 21.4966 78.1282 21.756 78.5211 21.9291C78.9141 22.1023 79.341 22.1847 79.7702 22.1702C81.9702 22.1702 83.2302 20.3102 83.2302 18.0802C83.2302 15.8502 82.0102 14.0002 79.8102 14.0002ZM79.5002 20.6002C79.2122 20.6031 78.9271 20.5437 78.6642 20.4262C78.4013 20.3086 78.1669 20.1357 77.977 19.9192C77.7871 19.7027 77.6462 19.4478 77.5639 19.1718C77.4816 18.8958 77.4599 18.6054 77.5002 18.3202V17.8502C77.5002 16.3702 78.3402 15.5602 79.5002 15.5602C80.8002 15.5602 81.5002 16.5602 81.5002 18.0802C81.5002 19.6002 80.7502 20.6002 79.5002 20.6002Z'
                fill='white'
            />
            <path
                d='M87.4296 19.6599L85.4996 14.1299H83.5996L86.4996 21.7199L86.1996 22.4599C86.1486 22.7134 86.0014 22.9373 85.7888 23.0846C85.5763 23.2319 85.3149 23.2912 85.0596 23.2499C84.8599 23.2645 84.6593 23.2645 84.4596 23.2499V24.7599C84.7012 24.7958 84.9453 24.8125 85.1896 24.8099C85.8139 24.8553 86.4344 24.6799 86.9425 24.3143C87.4506 23.9487 87.8142 23.4163 87.9696 22.8099L91.2096 14.1899H89.3196L87.4296 19.6599Z'
                fill='white'
            />
            <path
                d='M125.5 12.4297C125.105 12.4275 124.714 12.5031 124.348 12.6524C123.983 12.8017 123.65 13.0217 123.37 13.2997L123.23 12.6097H120.84V25.5297L123.56 24.9397V21.8097C124.101 22.2628 124.784 22.5106 125.49 22.5097C127.43 22.5097 129.21 20.9197 129.21 17.3997C129.21 14.1797 127.41 12.4297 125.5 12.4297ZM124.85 20.0597C124.611 20.0735 124.373 20.034 124.151 19.944C123.93 19.8541 123.731 19.716 123.57 19.5397V15.4297C123.732 15.2471 123.933 15.1029 124.158 15.0077C124.383 14.9126 124.626 14.8688 124.87 14.8797C125.87 14.8797 126.55 16.0097 126.55 17.4597C126.55 18.9097 125.86 20.0597 124.85 20.0597Z'
                fill='white'
            />
            <path
                d='M134.23 12.4297C131.61 12.4297 130.02 14.6897 130.02 17.5397C130.02 20.9097 131.9 22.6197 134.58 22.6197C135.625 22.6367 136.658 22.3854 137.58 21.8897V19.6397C136.743 20.0618 135.816 20.2745 134.88 20.2597C133.8 20.2597 132.88 19.8697 132.74 18.5597H138.12C138.12 18.4097 138.12 17.8197 138.12 17.5597C138.21 14.6897 136.85 12.4297 134.23 12.4297ZM132.76 16.4997C132.76 15.2397 133.53 14.7097 134.21 14.7097C134.89 14.7097 135.61 15.2397 135.61 16.4997H132.76Z'
                fill='white'
            />
            <path
                d='M113.5 13.36L113.33 12.54H111.01V22.25H113.69V15.67C113.932 15.3762 114.258 15.1632 114.624 15.0595C114.99 14.9559 115.38 14.9665 115.74 15.09V12.54C115.331 12.3798 114.877 12.3747 114.464 12.5257C114.052 12.6768 113.709 12.9735 113.5 13.36Z'
                fill='white'
            />
            <path
                d='M99.96 15.4601C99.96 15.0201 100.32 14.8501 100.89 14.8501C101.835 14.8704 102.761 15.1174 103.59 15.5701V12.9401C102.732 12.5934 101.815 12.4201 100.89 12.4301C98.68 12.4301 97.21 13.6101 97.21 15.5901C97.21 18.6901 101.35 18.1901 101.35 19.5201C101.35 20.0401 100.91 20.2101 100.35 20.2101C99.2934 20.1484 98.266 19.8401 97.35 19.3101V22.0001C98.2941 22.4212 99.3162 22.6392 100.35 22.6401C102.61 22.6401 104.17 21.4901 104.17 19.4801C104.12 16.1201 99.96 16.7201 99.96 15.4601Z'
                fill='white'
            />
            <path
                d='M107.78 10.2402L105.13 10.8202V19.7502C105.116 20.1266 105.179 20.5017 105.316 20.8528C105.452 21.2039 105.658 21.5235 105.922 21.7921C106.186 22.0607 106.502 22.2727 106.851 22.4151C107.199 22.5575 107.573 22.6273 107.95 22.6202C108.607 22.6496 109.261 22.5228 109.86 22.2502V20.0002C109.51 20.1502 107.8 20.6602 107.8 19.0002V15.0002H109.86V12.6602H107.8L107.78 10.2402Z'
                fill='white'
            />
            <path d='M116.75 11.6997L119.48 11.1297V8.96973L116.75 9.53973V11.6997Z' fill='white' />
            <path d='M119.48 12.6104H116.75V22.2504H119.48V12.6104Z' fill='white' />
        </g>
        <defs>
            <clipPath id='clip0_8958_58544'>
                <rect width='150' height='34' fill='white' transform='translate(0.5)' />
            </clipPath>
        </defs>
    </svg>
);

const CompletedSVG = (
    <svg width='250' height='200' viewBox='0 0 250 200' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M63 134H154C154.515 134 155.017 133.944 155.5 133.839C155.983 133.944 156.485 134 157 134H209C212.866 134 216 130.866 216 127C216 123.134 212.866 120 209 120H203C199.134 120 196 116.866 196 113C196 109.134 199.134 106 203 106H222C225.866 106 229 102.866 229 99C229 95.134 225.866 92 222 92H200C203.866 92 207 88.866 207 85C207 81.134 203.866 78 200 78H136C139.866 78 143 74.866 143 71C143 67.134 139.866 64 136 64H79C75.134 64 72 67.134 72 71C72 74.866 75.134 78 79 78H39C35.134 78 32 81.134 32 85C32 88.866 35.134 92 39 92H64C67.866 92 71 95.134 71 99C71 102.866 67.866 106 64 106H24C20.134 106 17 109.134 17 113C17 116.866 20.134 120 24 120H63C59.134 120 56 123.134 56 127C56 130.866 59.134 134 63 134ZM226 134C229.866 134 233 130.866 233 127C233 123.134 229.866 120 226 120C222.134 120 219 123.134 219 127C219 130.866 222.134 134 226 134Z'
            fill='#D1FFE4'
        />
        <path d='M172.5 45V51' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path d='M172.5 59V65' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path d='M162.5 55H168.5' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path d='M176.5 55H182.5' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path d='M89.5 148V152' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path d='M89.5 160V164' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path d='M81.5 156H85.5' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path d='M93.5 156H97.5' stroke='#00C853' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M160.711 131.126C162.102 129.423 163.352 127.642 164.461 125.799C166.901 121.744 168.66 117.388 169.734 112.897C170.821 108.356 171.209 103.676 170.894 99.0296C170.62 94.9756 169.811 90.9475 168.465 87.0598C167.131 83.2028 165.268 79.484 162.875 76.015C161.45 73.9479 159.836 71.9696 158.034 70.1036C155.388 67.364 152.486 65.0219 149.402 63.0787C146.089 60.9908 142.566 59.3634 138.926 58.1984C134.368 56.7393 129.628 56.0054 124.886 56C121.197 55.9959 117.507 56.4328 113.902 57.3125C109.845 58.3028 105.895 59.8543 102.177 61.9692C98.9076 63.8284 95.817 66.123 92.988 68.8547C89.4328 72.2875 86.553 76.1572 84.3512 80.2985C82.1956 84.3531 80.6899 88.6682 79.8366 93.0887C78.9574 97.6429 78.7706 102.309 79.2789 106.918C79.9378 112.891 81.7643 118.768 84.7644 124.179C86.684 127.641 89.0842 130.912 91.9663 133.896C96.5416 138.634 101.883 142.183 107.604 144.535C114.562 147.397 122.081 148.489 129.465 147.8C135.662 147.221 141.765 145.387 147.361 142.29'
            fill='#D1FFE4'
        />
        <path
            d='M160.711 131.126C162.102 129.423 163.352 127.642 164.461 125.799C166.901 121.744 168.66 117.388 169.734 112.897C170.821 108.356 171.209 103.676 170.894 99.0296C170.62 94.9756 169.811 90.9475 168.465 87.0598C167.131 83.2028 165.268 79.484 162.875 76.015C161.45 73.9479 159.836 71.9696 158.034 70.1036C155.388 67.364 152.486 65.0219 149.402 63.0787C146.089 60.9908 142.566 59.3634 138.926 58.1984C134.368 56.7393 129.628 56.0054 124.886 56C121.197 55.9959 117.507 56.4328 113.902 57.3125C109.845 58.3028 105.895 59.8543 102.177 61.9692C98.9076 63.8284 95.817 66.123 92.988 68.8547C89.4328 72.2875 86.553 76.1572 84.3512 80.2985C82.1956 84.3531 80.6899 88.6682 79.8366 93.0887C78.9574 97.6429 78.7706 102.309 79.2789 106.918C79.9378 112.891 81.7643 118.768 84.7644 124.179C86.684 127.641 89.0842 130.912 91.9663 133.896C96.5416 138.634 101.883 142.183 107.604 144.535C114.562 147.397 122.081 148.489 129.465 147.8C135.662 147.221 141.765 145.387 147.361 142.29'
            stroke='#00AD48'
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M150.701 140.378C153.607 138.508 156.191 136.175 157.872 134.291L150.701 140.378Z'
            fill='white'
        />
        <path
            d='M150.701 140.378C153.607 138.508 156.191 136.175 157.872 134.291'
            stroke='#00C853'
            strokeWidth='2.5'
            strokeLinecap='round'
        />
        <path
            d='M167 104.617C166.714 104.622 166.427 104.625 166.14 104.625C140.886 104.625 120.209 84.9362 118.476 60C98.3752 63.1706 83 80.6526 83 101.743C83 125.081 101.825 144 125.048 144C147.309 144 165.53 126.615 167 104.617Z'
            fill='#A2FFC9'
        />
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M109.023 100.373C107.8 99.2433 105.903 99.33 104.785 100.567C103.668 101.803 103.753 103.721 104.977 104.851L119.096 117.889C120.808 119.47 123.465 119.349 125.029 117.618C125.095 117.544 125.095 117.544 125.158 117.469L145.307 92.9716C146.366 91.6837 146.192 89.7716 144.918 88.7008C143.644 87.63 141.752 87.806 140.693 89.0939L121.75 112.125L109.023 100.373Z'
            fill='#D1FFE4'
            stroke='#00AD48'
            strokeWidth='2.5'
            strokeLinecap='round'
        />
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M110.824 64.4127C108.874 65.1444 106.969 66.0334 105.129 67.0802C102.269 68.7069 99.5652 70.7145 97.0903 73.1045C96.0758 74.0842 95.1241 75.1046 94.2354 76.1607M91.4854 79.8381C90.7788 80.906 90.1284 82.0007 89.5345 83.1179C89.0959 83.9431 88.688 84.7807 88.3109 85.6288'
            fill='white'
        />
        <path
            d='M111.263 65.5831C111.91 65.3406 112.237 64.62 111.995 63.9737C111.752 63.3273 111.032 62.9999 110.385 63.2424L111.263 65.5831ZM105.129 67.0802L105.747 68.1667L105.129 67.0802ZM97.0903 73.1045L96.222 72.2054L97.0903 73.1045ZM93.279 75.3558C92.8345 75.884 92.9023 76.6726 93.4306 77.1171C93.9588 77.5616 94.7473 77.4937 95.1918 76.9655L93.279 75.3558ZM92.5278 80.528C92.9088 79.9523 92.751 79.1767 92.1752 78.7957C91.5995 78.4147 90.824 78.5726 90.443 79.1483L92.5278 80.528ZM89.5345 83.1179L90.6383 83.7047L90.6383 83.7047L89.5345 83.1179ZM87.1687 85.1209C86.8882 85.7518 87.1722 86.4905 87.803 86.771C88.4338 87.0515 89.1726 86.7674 89.4531 86.1366L87.1687 85.1209ZM110.385 63.2424C108.374 63.997 106.409 64.9139 104.511 65.9937L105.747 68.1667C107.529 67.1529 109.374 66.2918 111.263 65.5831L110.385 63.2424ZM104.511 65.9937C101.561 67.6716 98.7728 69.742 96.222 72.2054L97.9586 74.0037C100.358 71.687 102.977 69.7421 105.747 68.1667L104.511 65.9937ZM96.222 72.2054C95.1764 73.215 94.1954 74.2669 93.279 75.3558L95.1918 76.9655C96.0529 75.9424 96.9751 74.9535 97.9586 74.0037L96.222 72.2054ZM90.443 79.1483C89.7141 80.2497 89.0434 81.3788 88.4308 82.5312L90.6383 83.7047C91.2135 82.6225 91.8434 81.5623 92.5278 80.528L90.443 79.1483ZM88.4308 82.5312C87.9783 83.3824 87.5576 84.2462 87.1687 85.1209L89.4531 86.1366C89.8183 85.3151 90.2134 84.5039 90.6383 83.7047L88.4308 82.5312Z'
            fill='#00C853'
        />
    </svg>
);

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ device }) => {
    return {
        '& .MuiDialog-paper > .MuiBox-root': {
            width: device === 'tablet' ? '700px' : '900px',
        },
    };
});

const Grid = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: theme.spacing(4),
        alignItems: 'center',
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(8, 7),
        boxShadow: theme.palette.ensightful.shadows[1],
    };
});

const GridBox = styled(Box)(({ device }) => {
    return {
        ...(device === 'mobile'
            ? {
                  display: 'flex',
                  flexDirection: 'column',
              }
            : {
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  margin: theme.spacing(-7),
                  width: 'calc(100% + 28px + 28px)',
              }),
    };
});

const LeftStack = styled(Stack)(({ theme, device }) => {
    return {
        padding: device === 'mobile' ? 0 : theme.spacing(7),
    };
});

const RightStack = styled(Stack)(({ theme, device }) => {
    return {
        ...(device === 'mobile' && {
            display: 'none',
        }),
        padding: theme.spacing(7),
        background: theme.palette.ensightful.primary[50],
    };
});

const InputStack = styled(Stack)(({ theme, device }) => {
    return {
        marginTop: device === 'mobile' ? theme.spacing(5) : theme.spacing(15),
    };
});

export default PaymentDialog;
